import cloneDeep from 'lodash/cloneDeep'

export function numberWithCommas(x) {
  if (typeof x !== 'number') return ''
  let parts = x.toString().split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return parts.join('.')
}

export function numberWithCommasDecimals(x, dec = 2) {
  if (typeof x === 'string') x = parseFloat(x)
  if (isNaN(x)) return ''
  if (typeof x !== 'number' || typeof dec !== 'number') return ''
  if (Math.abs(x) < 0.00001) x = 0

  let parts = x.toFixed(dec).split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return parts.join('.')
}

export function parseLocaleNumber(value) {
  if (value == null) return NaN
  if (typeof value === 'number') return value

  if (typeof value !== 'string') return NaN

  const str = value.trim()

  // If both separators exist, last one is the decimal
  const hasComma = str.includes(',')
  const hasDot = str.includes('.')

  if (hasComma && hasDot) {
    // whichever appears LAST is the decimal separator
    if (str.lastIndexOf(',') > str.lastIndexOf('.')) {
      // Spanish style: 1.234,56
      return Number(str.replace(/\./g, '').replace(',', '.'))
    } else {
      // English style: 1,234.56
      return Number(str.replace(/,/g, ''))
    }
  }

  // Only comma → decimal
  if (hasComma) {
    return Number(str.replace(',', '.'))
  }

  // Only dot → decimal
  return Number(str)
}

export function numberWithCommasDecimalsCompact(x, dec = 2) {
  if (typeof x === 'string') x = parseFloat(x)
  if (typeof x !== 'number' || isNaN(x) || typeof dec !== 'number') return ''
  if (Math.abs(x) < 0.00001) x = 0

  // Round to desired decimals first
  const factor = Math.pow(10, dec)
  const rounded = Math.round(x * factor) / factor

  // Convert to string with exactly 'dec' decimal places
  let str = rounded.toFixed(dec)

  // Split into integer and decimal parts
  let [integer, decimal = ''] = str.split('.')

  // Add thousand separators to integer part
  integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  // If decimal part is all zeros → drop decimal part completely
  if (decimal && /^0+$/.test(decimal)) {
    return integer
  }

  // Otherwise keep the decimal part (it has significant digits)
  return integer + (decimal ? '.' + decimal : '')
}

export function numberWithCommasMaxDecimals(x, dec = 2) {
  if (typeof x === 'string') x = parseFloat(x)
  if (typeof x !== 'number' || isNaN(x) || typeof dec !== 'number') return ''
  if (Math.abs(x) < 0.00001) x = 0

  const factor = Math.pow(10, dec)
  const rounded = Math.round(x * factor) / factor

  let [integer, decimal] = String(rounded).split('.')

  integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  return decimal ? `${integer}.${decimal}` : integer
}

export function replaceEscapeSymbols(str) {
  if (!str) return ''
  if (typeof str !== 'string') return ''
  str = str.replace('&dq;', '"')
  str = str.replace('&sq;', "'")
  str = str.replace('&amp;', '&')
  str = str.replace('&gt;', '>')
  str = str.replace('&lt;', '<')
  str = str.replace('?', '')
  return str
}

export function camelCaseToLabel(camelCaseString) {
  if (typeof camelCaseString !== 'string') return ''
  if (camelCaseString === '') return ''
  camelCaseString = camelCaseString.trim()

  let label = camelCaseString.replace(/([A-Z])/g, ' $1').toLowerCase()
  label = label.charAt(0).toUpperCase() + label.slice(1)
  return label
}

export function removeSpecialSymbols(str) {
  if (!str) return ''
  if (typeof str !== 'string') return ''
  str = str.replace("'", '')
  str = str.replace('"', '')
  str = str.replace('\\', '')
  str = str.replace(':', '')
  str = str.replace('#', '')
  str = str.replace('.', '_')
  return str
}

export function splitDateString(inputStr, delimiter) {
  let splitStr = inputStr.split(delimiter)

  let output = { day: -1, month: -1, year: -1 }
  splitStr.forEach((val, i) => {
    if (val.substring(0, 1) === '0') val = val.substring(1)

    if (i === 0) output.year = val
    if (i === 1) output.month = val
    if (i === 2) output.day = val
  })

  return output
}

export function createShortDate(inputDate, delimiter, usFormat) {
  let output = ''
  if (inputDate !== '') {
    let dateByComponents = splitDateString(inputDate, delimiter)
    if (dateByComponents.day > 0 && dateByComponents.month > 0) {
      if (usFormat === true) {
        output = dateByComponents.month + '/' + dateByComponents.day
      } else {
        output = dateByComponents.year + '/' + dateByComponents.month
      }
    }
  }

  return output
}

export function toQueryString(params) {
  return (
    '?' +
    Object.entries(params)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&')
  )
}

// process + sort arrays & objects, allows consistent comparison for query keys
export function normalizeParams(value) {
  if (value == null) return value
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'function') return '__fn__'
  if (Array.isArray(value)) return value.map(normalizeParams)
  if (typeof value !== 'object') return value

  const normalized = {}

  for (const key of Object.keys(value).sort()) {
    normalized[key] = value[key] === undefined ? null : normalizeParams(value[key])
  }

  return normalized
}

export function base64ToArrayBuffer(base64) {
  if (!base64) return null
  if (typeof base64 !== 'string') return null
  base64 = base64.replace('data:image/*;base64,', '')
  let binary_string = window.atob(base64)
  let len = binary_string.length
  let bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    bytes[i] = binary_string.charCodeAt(i)
  }
  return bytes.buffer
}

export function removeSymbolsFromName(input) {
  if (!input) return ''
  if (typeof input !== 'string') return ''

  input = input.replace('/', '')
  input = input.replace('\\', '')
  input = input.replace('"', '')
  input = input.replace("'", '')
  input = input.replace('#', '')
  input = input.replace(':', '')

  return input
}

export function uuidv4() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
  )
}

export function removeFileExt(filename) {
  const lastDotPosition = filename.lastIndexOf('.') // Find the position of the last dot in the filename

  // If there's no dot or it's the first character, return the original filename
  if (lastDotPosition === -1 || lastDotPosition === 0) {
    return filename
  }

  return filename.substring(0, lastDotPosition)
}

// remove middle, i.e.: 'Cerberus 14-28 - 3H (LH) - Lateral' -> 'Cerberus 14-28…Lateral'
export const shortenString = (string, maxLength = 24) => {
  if (!string || string.length <= maxLength) return string

  if (string.length <= maxLength) return string

  const keptLength = maxLength - 3
  const headLength = Math.ceil(keptLength * 0.6)
  const tailLength = keptLength - headLength

  const head = string.slice(0, headLength).trimEnd()
  const tail = string.slice(string.length - tailLength).trimStart()
  return `${head}…${tail}`
}

export function isEmpty(str) {
  return !str || str.trim() === ''
}

export function safeStr(v) {
  return v == null ? '' : String(v)
}

export function cleanPastedData(clipboardData, removeCommas = true) {
  if (!Array.isArray(clipboardData)) return []
  const lines = cloneDeep(clipboardData).filter((line) => Array.isArray(line))

  //Excel has a bug where regardless of the selected range there will be a blank row at the bottom
  const emptyLastRow = lines[lines.length - 1][0] === '' && lines[lines.length - 1].length === 1
  if (emptyLastRow) {
    lines.splice(lines.length - 1, 1)
  }

  if (!removeCommas) return lines

  //Remove thousands separators
  for (let i = 0; i < lines.length; i++) {
    for (let j = 0; j < lines[i].length; j++) {
      if (typeof lines[i][j] !== 'string') continue
      lines[i][j] = lines[i][j].replace(/,/g, '')
    }
  }

  return lines
}
